import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { type CSSProperties, type RefObject } from "react";

import { ScoutSkillSlugText } from "./ScoutSkillSlugText";

type ScoutComposerSkillHighlightProps = {
  value: string;
  backdropRef: RefObject<HTMLDivElement | null>;
  /** Pixel geometry of the textarea within the composer host (from layout sync). */
  geometry: { top: number; left: number; width: number; height: number } | null;
  /** Must match the textarea's padding so glyphs share the same content box. */
  padding: CSSProperties;
  /** Catalog slugs that should receive the tint (no leading `/`). */
  knownSlugs: ReadonlySet<string>;
};

/**
 * Mirror layer behind a transparent textarea so `/slug` tokens can be tinted
 * while the real input keeps caret, selection, and editing behavior.
 *
 * Glyph metrics must match the textarea exactly — any padding/font drift makes
 * the caret (textarea) race ahead of the visible letters (this layer).
 */
export function ScoutComposerSkillHighlight({
  value,
  backdropRef,
  geometry,
  padding,
  knownSlugs,
}: ScoutComposerSkillHighlightProps) {
  const theme = useTheme();

  if (!geometry) {
    return null;
  }

  return (
    <Box
      ref={backdropRef}
      aria-hidden
      sx={{
        position: "absolute",
        top: `${geometry.top}px`,
        left: `${geometry.left}px`,
        width: `${geometry.width}px`,
        height: `${geometry.height}px`,
        boxSizing: "border-box",
        margin: 0,
        border: 0,
        ...padding,
        overflow: "hidden",
        overflowX: "hidden",
        overflowY: "auto",
        pointerEvents: "none",
        whiteSpace: "pre-wrap",
        overflowWrap: "break-word",
        wordBreak: "break-word",
        lineHeight: 1.5,
        fontSize: theme.typography.body1.fontSize,
        fontFamily: theme.typography.fontFamily,
        letterSpacing: theme.typography.body1.letterSpacing,
        // Match textarea glyph metrics (divs can differ without these).
        fontWeight: theme.typography.body1.fontWeight,
        fontStyle: "normal",
        textRendering: "geometricPrecision",
        fontKerning: "none",
        fontVariantLigatures: "none",
        color: "text.primary",
        zIndex: 0,
        // Match textarea gutter so wrap points stay aligned.
        scrollbarGutter: "stable",
      }}
    >
      <ScoutSkillSlugText text={value} knownSlugs={knownSlugs} />
      {/* Trailing newline does not affect scrollHeight unless mirrored. */}
      {value.endsWith("\n") ? "\n" : null}
    </Box>
  );
}
